import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { TenantSettings } from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { applyColorPalette } from '../lib/color-utils';
import { useAuth } from './AuthContext';

/**
 * Contexte paramètres tenant (Phase 6 — US-ADM-01/05/06/09 + US-TR-06).
 *
 *  - Charge `GET /admin/tenant/settings` (best-effort : si l'user n'a pas
 *    la permission admin, on garde des défauts en mémoire).
 *  - Expose `refresh()` pour relire après une mutation.
 *  - Applique automatiquement la palette branding aux CSS variables au
 *    chargement et après chaque update (live preview cross-pages).
 *
 * Pour les super-admins plateforme (`is_platform_admin`), aucun paramètre
 * n'est chargé — le contexte renvoie `settings: null`.
 */

export type FullTenantSettings = Required<{
  [K in keyof TenantSettings]: NonNullable<TenantSettings[K]>;
}>;

interface TenantInfo {
  id: string;
  code: string;
  nom: string;
  sous_domaine: string | null;
  statut: string;
  contact_principal: unknown;
  logo_url: string | null;
  logo_signed_url: string | null;
  /**
   * URL signée du favicon si stocké en MinIO. Pour les URLs externes
   * (CDN), renvoie l'URL telle quelle. À utiliser dans `<link rel="icon">`.
   */
  favicon_signed_url: string | null;
}

export interface TenantSettingsApi {
  tenant: TenantInfo | null;
  settings: FullTenantSettings | null;
  loading: boolean;
  errorMessage: string | null;
  refresh(): Promise<void>;
  /** Ré-applique la palette en local SANS sauvegarde — pour la prévisualisation. */
  previewBranding(branding: FullTenantSettings['branding']): void;
}

const TenantSettingsCtx = createContext<TenantSettingsApi | null>(null);

export function TenantSettingsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [settings, setSettings] = useState<FullTenantSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Applique la palette aux CSS variables. Si un favicon HRef est fourni
   * (URL signée MinIO ou URL externe), met aussi à jour `<link rel="icon">`.
   */
  const applyBranding = useCallback(
    (branding: FullTenantSettings['branding'], faviconHref?: string | null) => {
      applyColorPalette('primary', branding.primary_color);
      applyColorPalette('secondary', branding.secondary_color);
      applyColorPalette('accent', branding.accent_color);
      const href = faviconHref ?? branding.favicon_url;
      if (href && /^https?:\/\//i.test(href)) {
        // URL externe utilisable directement.
        const link =
          document.querySelector<HTMLLinkElement>('link[rel="icon"]') ||
          (() => {
            const l = document.createElement('link');
            l.rel = 'icon';
            document.head.appendChild(l);
            return l;
          })();
        link.href = href;
      }
    },
    [],
  );

  const refresh = useCallback(async () => {
    if (!user || user.is_platform_admin) {
      setTenant(null);
      setSettings(null);
      return;
    }
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{
        data: TenantInfo & { settings: FullTenantSettings };
      }>('/admin/tenant/settings');
      const { settings: s, ...t } = res.data.data;
      const tenantInfo = t as TenantInfo;
      setTenant(tenantInfo);
      setSettings(s);
      applyBranding(s.branding, tenantInfo.favicon_signed_url);
    } catch (err) {
      // Tolérant : un user sans permission admin ne déclenche pas une bannière
      // d'erreur globale. On garde simplement settings = null.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 401 && status !== 403) {
        setErrorMessage(extractErrorMessage(err));
      }
      setTenant(null);
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [user, applyBranding]);

  useEffect(() => {
    refresh();
    // user.id : recharge sur changement d'utilisateur.
  }, [user?.id, refresh]);

  const value = useMemo<TenantSettingsApi>(
    () => ({
      tenant,
      settings,
      loading,
      errorMessage,
      refresh,
      previewBranding: applyBranding,
    }),
    [tenant, settings, loading, errorMessage, refresh, applyBranding],
  );

  return <TenantSettingsCtx.Provider value={value}>{children}</TenantSettingsCtx.Provider>;
}

export function useTenantSettings(): TenantSettingsApi {
  const ctx = useContext(TenantSettingsCtx);
  if (!ctx) {
    throw new Error('useTenantSettings must be used inside <TenantSettingsProvider>');
  }
  return ctx;
}
